import _ from 'lodash';

const asStrings = (arr) => _.map(arr || [], String);

const keepById = (items, ids, key) =>
  _.isEmpty(ids) ? (items || []) : _.filter(items, (i) => _.includes(asStrings(ids), String(i[key])));

export const catalogClasses = (catalog) => catalog?.classes || [];

/**
 * The class Class Insights should land on before the teacher picks one.
 *
 * JED-959: the catalog now also lists classes with no submissions, and the
 * backend sorts every active class ahead of any archived one. Taking classes[0]
 * blindly would strand a teacher whose live classes are empty but whose
 * archived class is full of work on a blank view. Prefer a class that has
 * something to show; fall back to the first when none do.
 */
export const defaultClassFor = (catalog) => {
  const classes = catalogClasses(catalog);
  if (classes.length === 0) return null;
  return _.find(classes, (c) => (c.submissionCount || 0) > 0) || classes[0];
};

export const selectedClasses = (catalog, filters) => {
  const classIds = filters.classIds || [];
  // Don't auto-convert empty to "all classes" - allow empty selection
  if (classIds.length === 0) {
    return [];
  }
  return keepById(catalogClasses(catalog), classIds, 'id');
};

const allSubjects = (classes) =>
  _.flatMap(classes, (c) => _.map(c.subjects || [], (s) => ({ ...s, classId: c.id })));

export const selectedSubjects = (classes, filters) =>
  keepById(allSubjects(classes), filters.subjectCodes, 'subjectCode');

const allTaskTypes = (subjects) => _.flatMap(subjects, (s) => s.taskTypes || []);

export const selectedTaskTypes = (subjects, filters) =>
  _.isEmpty(filters.taskTypes)
    ? allTaskTypes(subjects)
    : _.filter(allTaskTypes(subjects), (tt) => _.includes(filters.taskTypes, tt.taskType));

const allTasks = (taskTypes) => _.flatMap(taskTypes, (tt) => tt.tasks || []);

export const selectedTasks = (taskTypes, filters) =>
  keepById(allTasks(taskTypes), filters.taskIds, 'id');

const uniqById = (items) => _.uniqBy(items, (i) => String(i.id));

export const deriveClassOptions = (catalog) =>
  _.map(catalogClasses(catalog), (c) => ({
    id: String(c.id),
    label: c.title,
    isArchived: c.isArchived || false
  }));

export const deriveSubjectOptions = (classes) =>
  _.map(_.uniqBy(allSubjects(classes), 'subjectCode'),
    (s) => ({ id: s.subjectCode, label: s.subjectName }));

export const deriveTaskTypeOptions = (subjects) =>
  _.map(_.uniqBy(allTaskTypes(subjects), 'taskType'),
    (tt) => ({ id: tt.taskType, label: tt.taskType }));

export const deriveTaskOptions = (taskTypes) =>
  _.map(uniqById(allTasks(taskTypes)), (t) => ({ id: t.id, label: t.title }));

/**
 * Current students first, then anyone who has left a selected class but still
 * has work counted in it. A student who moved between two of the teacher's own
 * classes is current in one and former in the other, so order the current
 * entries ahead of the former ones BEFORE de-duping — otherwise uniqById could
 * keep the "past" copy and label an enrolled student as departed.
 */
export const deriveStudentOptions = (classes) => {
  const all = _.flatMap(classes, (c) => c.students || []);
  const currentFirst = [
    ..._.filter(all, (s) => !s.isFormer),
    ..._.filter(all, (s) => s.isFormer),
  ];
  return _.map(uniqById(currentFirst), (s) => ({
    id: String(s.id),
    label: s.isFormer ? `${s.name} (past)` : s.name,
    isFormer: !!s.isFormer,
  }));
};

const normalizeToolType = (value) => String(value || '').trim().toLowerCase();

const toToolOption = (tool, fallbackLabel) => {
  const id = String(tool?.id || '');
  const title = String(tool?.title || tool?.name || '').trim();
  const label = title || (id ? `${fallbackLabel} (${id})` : fallbackLabel);
  return { id, title: title || fallbackLabel, label };
};

const markingToolsByType = (tasks, type) =>
  _.filter(
    _.flatMap(tasks, (t) => t.markingTools || []),
    (m) => normalizeToolType(m?.type) === normalizeToolType(type)
  );

export const deriveCommentBanks = (tasks) =>
  _.map(uniqById(markingToolsByType(tasks, 'commentBank')),
    (m) => toToolOption(m, 'Untitled Comment Bank'));

export const deriveSuccessCriteria = (tasks) =>
  _.map(uniqById(markingToolsByType(tasks, 'successCriteria')),
    (m) => toToolOption(m, 'Untitled Success Criteria'));

export const deriveRubrics = (tasks) =>
  _.map(uniqById(markingToolsByType(tasks, 'rubric')),
    (m) => toToolOption(m, 'Untitled Rubric'));

export const deriveShortAnswers = (tasks) =>
  _.map(uniqById(markingToolsByType(tasks, 'shortAnswer')),
    (m) => toToolOption(m, 'Untitled Short Answer'));

export const markingToolsInScope = (catalog, filters) => {
  const classes = selectedClasses(catalog, filters);
  const subjects = selectedSubjects(classes, filters);
  const taskTypes = selectedTaskTypes(subjects, filters);
  const tasks = selectedTasks(taskTypes, filters);

  return {
    commentBanks: deriveCommentBanks(tasks),
    successCriteria: deriveSuccessCriteria(tasks),
    rubrics: deriveRubrics(tasks),
    shortAnswers: deriveShortAnswers(tasks),
  };
};

/**
 * One row per student across the given classes, tagged with the class they came
 * from. Used when the students list is showing more than one class at once.
 *
 * A student can be in several classes, and can be current in one while counting
 * as past in another, so current entries are ordered first and the survivor of
 * the de-dupe is the one that says they are still enrolled somewhere. Current
 * before past, each alphabetical.
 */
export const studentsAcrossClasses = (classes) => {
  const rows = _.flatMap(classes || [], (c) =>
    _.map(c.students || [], (s) => ({
      ...s,
      className: c.title || c.name || '',
    }))
  );
  const currentFirst = [
    ..._.filter(rows, (r) => !r.isFormer),
    ..._.filter(rows, (r) => r.isFormer),
  ];
  const unique = uniqById(currentFirst);
  const byName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''));
  return [
    ..._.filter(unique, (r) => !r.isFormer).sort(byName),
    ..._.filter(unique, (r) => r.isFormer).sort(byName),
  ];
};
